import React, { useState, useRef } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import EquationEditorModal from './EquationEditorModal';
import ChartBuilderModal from './ChartBuilderModal';

interface CKEditorWithToolsProps {
  data: string;
  onChange: (data: string) => void;
  config: any;
  editorKey?: string;
  placeholder?: string;
  /** Show chart button (only for full-toolbar editors) */
  showChartButton?: boolean;
}

/**
 * Wrapper around CKEditor that adds "Insert Equation" and optionally "Insert Chart"
 * buttons above the editor toolbar.
 */
const CKEditorWithTools: React.FC<CKEditorWithToolsProps> = ({
  data, onChange, config, editorKey, placeholder,
  showChartButton,
}) => {
  const [showEquationModal, setShowEquationModal] = useState(false);
  const [showChartModal, setShowChartModal] = useState(false);
  const editorRef = useRef<any>(null);

  const insertHtml = (html: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    // Insert HTML at cursor position using CKEditor's clipboard pipeline
    const viewFragment = editor.data.processor.toView(html);
    const modelFragment = editor.data.toModel(viewFragment);
    editor.model.change(() => {
      editor.model.insertContent(modelFragment);
    });
  };

  const handleEquationInsert = (html: string) => {
    insertHtml(html);
  };

  const handleChartInsert = (imgUrl: string) => {
    insertHtml(`<img src="${imgUrl}" alt="Chart" style="width:50%;" />`);
  };

  const mergedConfig = {
    ...config,
    ...(placeholder ? { placeholder } : {}),
  };

  return (
    <div>
      {/* Tool buttons above editor */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        <button
          type="button"
          onClick={() => setShowEquationModal(true)}
          style={{
            padding: '3px 10px', border: '1px solid var(--border-strong)', borderRadius: 4,
            background: 'var(--surface-card)', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            fontStyle: 'italic', fontFamily: 'serif', color: 'var(--text-body)',
          }}
          title="Insert Equation"
        >
          f<sub>x</sub>
        </button>
        {showChartButton && (
          <button
            type="button"
            onClick={() => setShowChartModal(true)}
            style={{
              padding: '3px 10px', border: '1px solid var(--border-strong)', borderRadius: 4,
              background: 'var(--surface-card)', cursor: 'pointer', fontSize: 13, color: 'var(--text-body)',
            }}
            title="Insert Chart"
          >
            &#x1F4CA;
          </button>
        )}
      </div>

      <CKEditor
        key={editorKey}
        editor={ClassicEditor}
        data={data}
        onChange={(_event: any, editor: any) => {
          onChange(editor.getData());
        }}
        onReady={(editor: any) => {
          editorRef.current = editor;
        }}
        onError={(error: any, details: any) => {
          console.error('CKEditor error:', error, details);
        }}
        config={mergedConfig}
      />

      <EquationEditorModal
        show={showEquationModal}
        onHide={() => setShowEquationModal(false)}
        onInsert={handleEquationInsert}
      />

      {showChartButton && (
        <ChartBuilderModal
          show={showChartModal}
          onHide={() => setShowChartModal(false)}
          onInsert={handleChartInsert}
        />
      )}
    </div>
  );
};

export default CKEditorWithTools;
